import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid2,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Divider
} from '@mui/material';
import {
    AccountBalance as PoolIcon,
    TrendingUp as TrendingUpIcon,
    Settings as SettingsIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { FeeMetrics, PositionHealth, PoolHealth, PortfolioOverview } from '../../types/DashboardTypes';
import FeeCollectionCard from './FeeCollectionCard';
import PositionHealthCard from './PositionHealthCard';
import PoolHealthCard from './PoolHealthCard';
import AutoCompoundConfigCard from './AutoCompoundConfigCard';
import FeeHistoryChart from './FeeHistoryChart';
import { useFeeManagement } from '../../hooks/useFeeManagement';
import { useAerodromeManagement } from '../../hooks/useAerodromeManagement';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`fee-tabpanel-${index}`}
            aria-labelledby={`fee-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `fee-tab-${index}`,
        'aria-controls': `fee-tabpanel-${index}`,
    };
}

interface FeeManagementDashboardProps {
    contractAddress?: string;
    dexType: 'uniswap' | 'aerodrome';
}

const FeeManagementDashboard: React.FC<FeeManagementDashboardProps> = ({
    contractAddress,
    dexType
}) => {
    const [tabValue, setTabValue] = useState(0);

    // Use the appropriate hook based on DEX type
    const uniswapData = useFeeManagement(dexType === 'uniswap' ? contractAddress : undefined);
    const aerodromeData = useAerodromeManagement(dexType === 'aerodrome' ? contractAddress : undefined);

    // Get data from the appropriate hook
    const {
        feeMetrics: rawFeeMetrics,
        positions: uniswapPositions,
        autoCompoundConfig: uniswapAutoConfig,
        loading: uniswapLoading,
        error: uniswapError
    } = uniswapData;

    const {
        feeMetrics: aerodromeRawFeeMetrics,
        pools: aerodromePools,
        autoCompoundConfig: aerodromeAutoConfig,
        loading: aerodromeLoading,
        error: aerodromeError
    } = aerodromeData;

    // Convert hook data to dashboard types
    const feeMetrics: FeeMetrics | null = dexType === 'uniswap' ? rawFeeMetrics : aerodromeRawFeeMetrics;
    const loading = dexType === 'uniswap' ? uniswapLoading : aerodromeLoading;
    const error = dexType === 'uniswap' ? uniswapError : aerodromeError;

    // Create portfolio overview from real data
    const portfolioOverview: PortfolioOverview | null = React.useMemo(() => {
        if (!feeMetrics) return null;

        if (dexType === 'uniswap') {
            return {
                totalPools: uniswapPositions.length,
                totalPositions: uniswapPositions.length,
                totalLPTokens: uniswapPositions.reduce((sum, pos) => sum + parseFloat(pos.liquidity), 0).toString(),
                totalValueETH: feeMetrics.totalValueETH,
                totalUnclaimedFeesETH: feeMetrics.totalValueETH,
                averagePoolShare: 0, // Not applicable for Uniswap V3
                autoCompoundPositions: uniswapAutoConfig?.enabled ? 1 : 0,
                lastUpdated: feeMetrics.lastUpdated
            };
        } else {
            return {
                totalPools: aerodromePools.length,
                totalPositions: aerodromePools.length,
                totalLPTokens: aerodromePools.reduce((sum, pool) => sum + parseFloat(pool.lpBalance), 0).toString(),
                totalValueETH: feeMetrics.totalValueETH,
                totalUnclaimedFeesETH: feeMetrics.totalValueETH,
                averagePoolShare: aerodromePools.length > 0 ?
                    aerodromePools.reduce((sum, pool) => sum + pool.sharePercentage, 0) / aerodromePools.length : 0,
                autoCompoundPositions: aerodromeAutoConfig?.enabled ? 1 : 0,
                lastUpdated: feeMetrics.lastUpdated
            };
        }
    }, [feeMetrics, dexType, uniswapPositions, aerodromePools, uniswapAutoConfig, aerodromeAutoConfig]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading fee management data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Paper elevation={2} sx={{ p: 3, m: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <TrendingUpIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h5" fontWeight="bold" color="error">
                        {dexType === 'uniswap' ? 'Uniswap V3' : 'Aerodrome V2'} Fee Management - Connection Error
                    </Typography>
                </Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        🚨 Contract Connection Failed
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                </Alert>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 152, 0, 0.08)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="warning.main" gutterBottom>
                        Troubleshooting Steps:
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        <li>Check that your wallet is connected to the correct network</li>
                        <li>Verify the contract is deployed at the expected address</li>
                        <li>Ensure your RPC provider is working correctly</li>
                        <li>Try refreshing the page or reconnecting your wallet</li>
                    </Box>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={2} sx={{ p: 3, m: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                    {dexType === 'uniswap' ? 'Uniswap V3' : 'Aerodrome V2'} Fee Management
                </Typography>
            </Box>

            {portfolioOverview && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                                Total Value
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {portfolioOverview.totalValueETH} ETH
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                                Unclaimed Fees
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                {portfolioOverview.totalUnclaimedFeesETH} ETH
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                                {dexType === 'uniswap' ? 'Positions' : 'Pools'}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {portfolioOverview.totalPositions}
                            </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Typography variant="caption" color="textSecondary">
                                Auto-Compound
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                {portfolioOverview.autoCompoundPositions} Active
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Box>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="fee management tabs">
                    <Tab
                        icon={<TrendingUpIcon />}
                        label="Fee Collection"
                        {...a11yProps(0)}
                    />
                    <Tab
                        icon={<PoolIcon />}
                        label={dexType === 'uniswap' ? 'Position Health' : 'Pool Health'}
                        {...a11yProps(1)}
                    />
                    <Tab
                        icon={<SettingsIcon />}
                        label="Auto-Compound"
                        {...a11yProps(2)}
                    />
                    <Tab
                        icon={<TimelineIcon />}
                        label="History"
                        {...a11yProps(3)}
                    />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12 }}>
                        {feeMetrics && (
                            <FeeCollectionCard
                                metrics={feeMetrics}
                                dexType={dexType}
                                contractAddress={contractAddress}
                            />
                        )}
                    </Grid2>
                </Grid2>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Grid2 container spacing={3}>
                    {dexType === 'uniswap' ? (
                        uniswapPositions.length > 0 ? (
                            uniswapPositions.map((position) => (
                                <Grid2 size={{ xs: 12, md: 6 }} key={position.tokenId}>
                                    <PositionHealthCard
                                        position={position}
                                        contractAddress={contractAddress}
                                    />
                                </Grid2>
                            ))
                        ) : (
                            <Grid2 size={{ xs: 12 }}>
                                <Alert severity="info" sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        📊 No Uniswap V3 Positions Found
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        No liquidity positions detected for this contract. This could mean:
                                    </Typography>
                                    <Box component="ul" sx={{ mt: 1, mb: 0, textAlign: 'left', display: 'inline-block' }}>
                                        <li>No positions have been created yet</li>
                                        <li>Positions exist but contract calls are failing</li>
                                        <li>Contract is not properly connected</li>
                                    </Box>
                                </Alert>
                            </Grid2>
                        )
                    ) : (
                        aerodromePools.length > 0 ? (
                            aerodromePools.map((pool, index) => (
                                <Grid2 size={{ xs: 12, md: 6 }} key={pool.pool}>
                                    <PoolHealthCard
                                        pool={pool}
                                        contractAddress={contractAddress}
                                    />
                                </Grid2>
                            ))
                        ) : (
                            <Grid2 size={{ xs: 12 }}>
                                <Alert severity="info" sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        🏊 No Aerodrome V2 Pools Found
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        No liquidity pools detected for this contract. This could mean:
                                    </Typography>
                                    <Box component="ul" sx={{ mt: 1, mb: 0, textAlign: 'left', display: 'inline-block' }}>
                                        <li>No pools have been created yet</li>
                                        <li>Pools exist but contract calls are failing</li>
                                        <li>Contract is not properly connected</li>
                                    </Box>
                                </Alert>
                            </Grid2>
                        )
                    )}
                </Grid2>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12 }}>
                        <AutoCompoundConfigCard
                            dexType={dexType}
                            contractAddress={contractAddress}
                            positions={dexType === 'uniswap' ? uniswapPositions : undefined}
                            pools={dexType === 'aerodrome' ? aerodromePools : undefined}
                        />
                    </Grid2>
                </Grid2>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12 }}>
                        <FeeHistoryChart
                            dexType={dexType}
                            contractAddress={contractAddress}
                        />
                    </Grid2>
                </Grid2>
            </TabPanel>
        </Paper>
    );
};

export default FeeManagementDashboard;
